import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpErrorResponse, HTTP_INTERCEPTORS } from '@angular/common/http';
import { throwError } from 'rxjs';
import { first, map, mergeMap, catchError, tap, switchMap } from 'rxjs/operators';
import * as i1 from '@angular/router';
import { createAction } from '@ngrx/store';

const TOKEN_SERVICE = new InjectionToken('TOKEN_SERVICE');
const AUTH_SERVICE = new InjectionToken('AUTH_SERVICE');
const ERROR_PAGE_URI = new InjectionToken('ERROR_PAGE_URI');

class TokenService {
}

const InterceptorSkipHeader = 'X-Skip-Interceptor';
const InterceptorSkipRefresh = 'X-Skip-Refresh';
class TokenInterceptor {
    constructor(tokenService) {
        this.tokenService = tokenService;
    }
    intercept(request, next) {
        const skipRefresh = request.headers.has(InterceptorSkipRefresh);
        if (skipRefresh) {
            request = request.clone({
                headers: request.headers.delete(InterceptorSkipRefresh),
            });
        }
        if (request.headers.has(InterceptorSkipHeader)) {
            return next.handle(request.clone({
                headers: request.headers.delete(InterceptorSkipHeader),
            }));
        }
        return this.tokenService.getToken().pipe(first(), map((token) => (token ? request.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : request)), mergeMap((req) => next.handle(req).pipe(catchError((error) => {
            if (error instanceof HttpErrorResponse && error.status === 401 && !skipRefresh) {
                return this.tokenService.refreshToken().pipe(tap((accessToken) => this.tokenService.setToken(accessToken)), switchMap(({ accessToken }) => {
                    const retryRequest = req.clone({
                        setHeaders: { Authorization: `Bearer ${accessToken}` },
                    });
                    return next.handle(retryRequest);
                }), catchError((refreshError) => {
                    // If we are here, we have an expired token and failed to get a refresh token.
                    this.tokenService.signout();
                    return throwError(refreshError);
                }));
            }
            return throwError(error);
        }))));
    }
}
TokenInterceptor.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: TokenInterceptor, deps: [{ token: TOKEN_SERVICE }], target: i0.ɵɵFactoryTarget.Injectable });
TokenInterceptor.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: TokenInterceptor });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: TokenInterceptor, decorators: [{
            type: Injectable
        }], ctorParameters: function () { return [{ type: TokenService, decorators: [{
                    type: Inject,
                    args: [TOKEN_SERVICE]
                }] }]; } });

class TokenInterceptorModule {
}
TokenInterceptorModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: TokenInterceptorModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
TokenInterceptorModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: TokenInterceptorModule, imports: [CommonModule] });
TokenInterceptorModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: TokenInterceptorModule, providers: [TokenInterceptor, { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true }], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: TokenInterceptorModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [],
                    providers: [TokenInterceptor, { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptor, multi: true }],
                }]
        }] });

class AuthService {
}

class RoleGuard {
    constructor(authService, errorPageUri, router) {
        this.authService = authService;
        this.errorPageUri = errorPageUri;
        this.router = router;
    }
    canActivate(next, state) {
        const allowedRoles = next.data.allowedRoles;
        return this.authService.isAuthorized(allowedRoles).pipe(map((isAuthorized) => {
            if (isAuthorized) {
                return true;
            }
            else {
                return this.router.parseUrl(this.errorPageUri);
            }
        }));
    }
}
RoleGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: RoleGuard, deps: [{ token: AUTH_SERVICE }, { token: ERROR_PAGE_URI }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
RoleGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: RoleGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: RoleGuard, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: AuthService, decorators: [{
                    type: Inject,
                    args: [AUTH_SERVICE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [ERROR_PAGE_URI]
                }] }, { type: i1.Router }]; } });

class AuthModule {
}
AuthModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: AuthModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
AuthModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: AuthModule, imports: [CommonModule] });
AuthModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: AuthModule, providers: [RoleGuard], imports: [[CommonModule]] });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.2.5", ngImport: i0, type: AuthModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [CommonModule],
                    declarations: [],
                    providers: [RoleGuard],
                }]
        }] });

const refreshToken = createAction('[Auth] Refresh Token');

/**
 * Generated bundle index. Do not edit.
 */

export { AUTH_SERVICE, AuthModule, AuthService, ERROR_PAGE_URI, RoleGuard, TOKEN_SERVICE, TokenInterceptorModule, TokenService, refreshToken };
